/**
 * FoodPulseArticles — tile directory of every article propagated from
 * foodpulse.net (food-waste / fruits / vegetables / news). Filters
 * by category chip and free-text search.
 *
 * Route: /food-pulse/articles                  → all
 *        /food-pulse/articles?category=fruits  → single category
 * Backend: GET /api/food-articles, /api/food-articles/categories
 */
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Search, Sparkles, Newspaper, Leaf, Apple, Trash2, HeartPulse } from "lucide-react";
import SeoHead from "@/components/SeoHead";

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_META = {
  "food-waste": { label: "Food Waste", icon: Trash2,     kicker: "less waste" },
  "fruits":     { label: "Fruits",     icon: Apple,      kicker: "seasonal" },
  "vegetables": { label: "Vegetables", icon: Leaf,       kicker: "harvest" },
  "health":     { label: "Health",     icon: HeartPulse, kicker: "wellbeing" },
  "news":       { label: "News",       icon: Newspaper,  kicker: "community" },
};

const CATEGORY_ORDER = ["food-waste", "fruits", "vegetables", "health", "news"];

function prettyDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  } catch { return ""; }
}

export default function FoodPulseArticles() {
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);

  const q = params.get("q") || "";
  const category = params.get("category") || "";

  useEffect(() => {
    document.title = category
      ? `${CATEGORY_META[category]?.label || "Articles"} — Food Pulse`
      : "Food Pulse — Articles from the desk";
  }, [category]);

  // Load per-category counts once
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/api/food-articles/categories`);
        if (cancelled) return;
        const map = {};
        (res.data.by_category || []).forEach((c) => { map[c.category] = c.count; });
        setCounts({ ...map, total: res.data.total });
      } catch { /* optional */ }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const res = await axios.get(`${API}/api/food-articles`, {
          params: { q: q || undefined, category: category || undefined, limit: 60 },
        });
        if (cancelled) return;
        setItems(res.data.items || []);
        setTotal(res.data.total || 0);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [q, category]);

  const setParam = (key, val) => {
    const next = new URLSearchParams(params);
    if (val) next.set(key, val); else next.delete(key);
    setParams(next, { replace: true });
  };

  return (
    <div className="min-h-screen" style={{ background: "#FBF7F0" }} data-testid="foodpulse-articles">
      <SeoHead
        section="food-pulse"
        title={category ? `${CATEGORY_META[category]?.label} — Food Pulse` : "Food Pulse — Articles"}
        description="Articles, recipes, and community stories from the Food Pulse network. Fight food waste, celebrate seasonal produce, and share what you cook."
      />

      {/* Hero — classic serif treatment lifted from foodpulse.net */}
      <section style={{ background: "#F0E2C7" }} className="border-b" data-testid="articles-hero">
        <div className="max-w-6xl mx-auto px-4 py-14">
          <div className="text-[10px] sm:text-xs font-mono uppercase tracking-widest mb-4" style={{ color: "#8B5E10" }}>
            — Food Pulse · {category ? CATEGORY_META[category]?.kicker : "articles from the desk"}
          </div>
          <h1
            className="font-display leading-[1.05] tracking-tight text-[44px] sm:text-[64px] lg:text-[80px] mb-4"
            style={{ color: "#1A1613" }}
          >
            {category === "food-waste" ? (<>Save the harvest. <em style={{ color: "#B25D38", fontStyle: "italic" }}>Share the goodness.</em></>) :
             category === "fruits"     ? (<>From orchard <em style={{ color: "#B25D38", fontStyle: "italic" }}>to your kitchen.</em></>) :
             category === "vegetables" ? (<>Everyday greens, <em style={{ color: "#B25D38", fontStyle: "italic" }}>everyday miracles.</em></>) :
             category === "health"     ? (<>Eat well, <em style={{ color: "#B25D38", fontStyle: "italic" }}>live well.</em></>) :
             category === "news"       ? (<>The community, <em style={{ color: "#B25D38", fontStyle: "italic" }}>told in food.</em></>) :
                                         (<>Share a recipe. <em style={{ color: "#B25D38", fontStyle: "italic" }}>Feed a neighbour.</em></>)}
          </h1>
          <p className="text-sm sm:text-base max-w-2xl" style={{ color: "#4B3A20" }}>
            Discover local food deals, fight food waste, and explore recipes using surplus ingredients. Join our community of farmers, kitchens, and everyday cooks — from every corner of the Food Pulse network.
          </p>
        </div>
      </section>

      {/* Category chips + search */}
      <div className="max-w-6xl mx-auto px-4 pt-8">
        <div className="flex flex-wrap gap-2 items-center" data-testid="articles-chips">
          <button
            type="button"
            onClick={() => setParam("category", "")}
            data-testid="articles-cat-all"
            className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${category === "" ? "text-white" : "hover:bg-white"}`}
            style={category === ""
              ? { background: "#1A1613", borderColor: "#1A1613" }
              : { background: "#fff", borderColor: "#C79A3F", color: "#3E2A0A" }}
          >
            All {counts.total ? `· ${counts.total}` : ""}
          </button>
          {CATEGORY_ORDER.map((c) => {
            const meta = CATEGORY_META[c];
            const Icon = meta.icon;
            const active = category === c;
            const n = counts[c];
            return (
              <button
                key={c}
                type="button"
                onClick={() => setParam("category", c)}
                data-testid={`articles-cat-${c}`}
                className={`inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border transition-colors ${active ? "text-white" : "hover:bg-white"}`}
                style={active
                  ? { background: "#B25D38", borderColor: "#B25D38" }
                  : { background: "#fff", borderColor: "#C79A3F", color: "#3E2A0A" }}
              >
                <Icon className="w-3.5 h-3.5" />
                {meta.label} {n ? `· ${n}` : ""}
              </button>
            );
          })}
          <div className="ml-auto flex items-center gap-2 rounded-full border bg-white px-3 py-1.5 min-w-[200px]"
               style={{ borderColor: "#C79A3F" }}>
            <Search className="w-4 h-4 opacity-60" />
            <input
              type="search"
              data-testid="articles-search"
              value={q}
              onChange={(e) => setParam("q", e.target.value)}
              placeholder="Search articles…"
              className="w-full bg-transparent outline-none text-sm"
            />
          </div>
        </div>
      </div>

      {/* Grid */}
      <section className="max-w-6xl mx-auto px-4 py-8">
        {loading ? (
          <div className="text-sm opacity-70" data-testid="articles-loading">Loading articles…</div>
        ) : items.length === 0 ? (
          <div className="rounded-2xl border p-10 text-center bg-white"
               style={{ borderColor: "#C79A3F" }} data-testid="articles-empty">
            <Sparkles className="w-6 h-6 mx-auto mb-2" style={{ color: "#8B5E10" }} />
            <div className="font-display text-lg mb-1">No articles match this filter yet.</div>
            <div className="text-sm opacity-70">Articles are being propagated from foodpulse.net — check back in a minute.</div>
          </div>
        ) : (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="articles-grid">
            {items.map((a) => (
              <Link
                key={a.slug}
                to={`/food-pulse/articles/${a.slug}`}
                className="group bg-white rounded-2xl overflow-hidden border hover:shadow-md transition-shadow"
                style={{ borderColor: "#E9D8B4" }}
                data-testid={`article-card-${a.slug}`}
              >
                {a.cover_image ? (
                  <div className="aspect-[16/10] overflow-hidden bg-black/5">
                    <img
                      src={a.cover_image}
                      alt={a.title}
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform"
                    />
                  </div>
                ) : (
                  <div className="aspect-[16/10] flex items-center justify-center"
                       style={{ background: "linear-gradient(135deg,#F0E2C7,#FBF7F0)" }}>
                    <Newspaper className="w-8 h-8 opacity-30" style={{ color: "#8B5E10" }} />
                  </div>
                )}
                <div className="p-5">
                  {(a.tag || a.category) && (
                    <div className="text-[10px] font-mono uppercase tracking-widest mb-2" style={{ color: "#B25D38" }}>
                      {a.tag || CATEGORY_META[a.category]?.label || a.category}
                    </div>
                  )}
                  <h3 className="font-display text-xl leading-tight" style={{ color: "#1A1613" }}>
                    {a.title}
                  </h3>
                  {a.excerpt && (
                    <p className="text-sm mt-2 opacity-80 line-clamp-3">{a.excerpt}</p>
                  )}
                  {a.published_at && (
                    <div className="mt-3 text-xs opacity-60">{prettyDate(a.published_at)}</div>
                  )}
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
